import React, { useState } from 'react';
import { formatDateTime, formatMoney } from '../utils/helpers';

const STATUS_CONFIG = {
    draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
    pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending approval' },
    approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
    processed: { color: 'bg-purple-100 text-purple-800', label: 'Paid' },
    rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
};

const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
            {cfg.label}
        </span>
    );
};

const Row = ({ label, value, bold }) => (
    <div className="flex justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className={bold ? 'font-bold text-gray-900' : 'text-gray-900'}>{value}</span>
    </div>
);

const InvoiceCard = ({
    invoice,
    onDownload,
    onView,
    onViewService,
    onSubmit,
    onRegeneratePdf,
    downloading = false,
    busy = false,
}) => {
    const isDraft = invoice.status === 'draft';
    const [extra, setExtra] = useState(String(invoice.extra_charges_amount ?? 0));
    const [notes, setNotes] = useState(invoice.extra_charges_notes || '');

    const money = (v) => formatMoney(v, invoice?.currency);
    const feeLabel = invoice.commission_percent != null
        ? `Platform fee (${invoice.commission_percent}%)`
        : 'Platform fee';
    const feeEx = Number(invoice.platform_fee_ex_gst ?? 0);
    const showVendorFee = feeEx > 0 || Number(invoice.platform_fee_amount ?? 0) > 0;
    const gstPct = Math.round(Number(invoice.gst_rate || 0.15) * 100);

    const submit = () => {
        const amt = Number(extra);
        if (Number.isNaN(amt) || amt < 0) {
            alert('Extra charges must be zero or a positive number.');
            return;
        }
        onSubmit(invoice, amt, notes);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 hover:shadow-md transition-shadow">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                            <button
                                onClick={() => onView && onView(invoice.id)}
                                className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                            >
                                Invoice #{invoice.id}
                            </button>
                        </h3>
                        <StatusBadge status={invoice.status} />
                    </div>
                    {invoice.service_number && (
                        <button
                            onClick={() => onViewService && invoice.service_id && onViewService(invoice.service_id)}
                            className="text-sm text-gray-600 hover:text-blue-700 hover:underline"
                        >
                            Service {invoice.service_number}
                        </button>
                    )}
                </div>
                <div className="sm:text-right shrink-0">
                    <div className="text-xl md:text-2xl font-bold text-gray-900">
                        {money(invoice.net_amount)}
                    </div>
                    <div className="text-sm text-gray-500">
                        Created: {formatDateTime(invoice.created_at)}
                    </div>
                </div>
            </div>

            <div className="space-y-2 mb-4 p-3 md:p-4 bg-gray-50 rounded-lg">
                <Row label="Service rate (ex GST)" value={money(invoice.rate_ex_gst ?? invoice.rate_amount)} />
                {showVendorFee && (
                    <Row
                        label={feeLabel}
                        value={`- ${money(invoice.platform_fee_ex_gst ?? invoice.platform_fee_amount)}`}
                    />
                )}
                {Number(invoice.extra_charges_amount || invoice.extra_ex_gst || 0) > 0 && (
                    <Row
                        label="Extra charges"
                        value={money(invoice.extra_charges_amount ?? invoice.extra_ex_gst)}
                    />
                )}
                <Row
                    label="Total"
                    value={money(invoice.amount_after_fee_ex_gst ?? invoice.gross_ex_gst)}
                />
                <Row label={`GST (${gstPct}%)`} value={money(invoice.gst_amount ?? 0)} />
                <Row label="Net payable" value={money(invoice.net_amount)} bold />
                {invoice.extra_charges_notes && (
                    <p className="text-xs text-gray-500 pt-1">Note: {invoice.extra_charges_notes}</p>
                )}
            </div>

            {isDraft && (
                <div className="mb-4 p-3 md:p-4 bg-blue-50 rounded-lg space-y-3">
                    <p className="text-sm text-gray-700">
                        Add any extra charges for this job, then submit for platform approval.
                    </p>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Extra charges</label>
                        <input
                            type="number" min="0" step="0.01"
                            value={extra}
                            onChange={(e) => setExtra(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <input
                            type="text"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Reason for extra charges (optional)"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                    </div>
                    <button
                        onClick={submit}
                        disabled={busy}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        {busy ? 'Submitting…' : 'Submit Invoice'}
                    </button>
                </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                {invoice.pdf_path ? (
                    <button
                        onClick={() => onDownload(invoice.id)}
                        disabled={downloading}
                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        {downloading ? 'Downloading…' : 'Download PDF'}
                    </button>
                ) : (
                    <button
                        onClick={() => onRegeneratePdf(invoice.id)}
                        disabled={busy}
                        className="flex-1 border border-blue-600 text-blue-600 px-4 py-2 rounded-md hover:bg-blue-50 disabled:opacity-50"
                    >
                        {busy ? 'Working…' : 'Generate PDF'}
                    </button>
                )}
                {onView && (
                    <button
                        onClick={() => onView(invoice.id)}
                        className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                    >
                        View Details
                    </button>
                )}
            </div>
        </div>
    );
};

export default InvoiceCard;
